// packages/utils/src/social-scoring.ts
// FRAME v3 — Social Calibration Engine
// Mode-specific dimensions that score photos for their SOCIAL CONTEXT.
// This is FRAME's deepest moat: big tech can't ship opinionated social scoring.

import type {
  SocialCalibrationScores,
  SocialCalibrationKey,
  SocialCalibrationWeightProfile,
} from '@frame/scoring-types';
import { SOCIAL_CALIBRATION_WEIGHTS } from '@frame/scoring-types';

// ─── Input Signals ─────────────────────────────────────────
// These are computed from CV primitives (face detection, scene classification,
// color analysis, object detection) but applied in socially calibrated ways.

export interface SocialSignals {
  // Face/people signals
  faceCount: number;
  primaryFaceConfidence: number;    // 0-1
  smileIntensity: number;           // 0-1
  eyeContactStrength: number;       // 0-1
  bodyLanguageOpenness: number;     // 0-1 (arms open vs crossed)
  expressionGenuineness: number;    // 0-1 (Duchenne smile detection)

  // Scene signals
  sceneCategory: string;            // 'outdoor', 'restaurant', 'event', 'home', etc.
  sceneEnergy: number;              // 0-1 (party vs library)
  settingAspirationLevel: number;   // 0-1 (luxury restaurant vs fast food)
  hasAnimal: boolean;
  hasFood: boolean;

  // Visual signals
  colorVibrancy: number;            // 0-1
  contrastLevel: number;            // 0-1
  visualComplexity: number;         // 0-1
  uniquenessScore: number;          // 0-1 (embedding distance from "generic")
  dominantColorWarmth: number;      // 0-1 (warm = higher)

  // Subject presentation
  groomingScore: number;            // 0-1
  backgroundCleanness: number;      // 0-1
  outfitCoherence: number;          // 0-1 (style coherence)
  subjectProminence: number;        // 0-1 (how central is the subject)

  // Atmospheric
  atmosphericDepth: number;         // 0-1
  lightingMood: 'warm' | 'cool' | 'neutral' | 'dramatic';
  goldenHourDetected: boolean;

  // Context (for session-level dims)
  photoIndex?: number;              // position in set
  totalPhotos?: number;
  setCategories?: string[];         // scene categories across the set
  setColorPalettes?: Array<{ h: number; s: number; v: number }>;
}

// ─── Dating Dimensions (D1–D6) ─────────────────────────────

// Patent: Novel dimension — D1: Social proof signal scoring.
// Detects evidence of social life (friends, events, activities) from face count,
// scene category, and scene energy. No prior art for social proof as scoring axis.
export function scoreSocialProof(signals: SocialSignals): number {
  // Friends in photo = strong signal
  const friendSignal = signals.faceCount >= 2 && signals.faceCount <= 5
    ? 0.7 + Math.min(0.3, (signals.faceCount - 1) * 0.1)
    : signals.faceCount > 5 ? 0.6 : 0.2; // too many = crowd, not friends

  // Social setting bonus
  const socialSettings = ['restaurant', 'event', 'party', 'bar', 'concert', 'wedding', 'festival'];
  const settingSignal = socialSettings.includes(signals.sceneCategory) ? 0.8 : 0.4;

  // Activity energy
  const energySignal = signals.sceneEnergy;

  // Animal bonus (pets = approachable)
  const petBonus = signals.hasAnimal ? 0.3 : 0;

  const raw = friendSignal * 0.40 + settingSignal * 0.25 + energySignal * 0.20 + petBonus * 0.15;
  return Math.min(10, raw * 10);
}

// Patent: Novel dimension — D2: Lifestyle breadth scoring.
// Measures variety of settings and activities. Session-aware but scored per-photo.
export function scoreLifestyleBreadth(signals: SocialSignals): number {
  // Diverse scene category = good
  const categoryDiversity = signals.setCategories
    ? new Set(signals.setCategories).size / Math.max(signals.totalPhotos ?? 1, 1)
    : 0.5;

  // Outdoor vs indoor balance
  const isOutdoor = ['outdoor', 'beach', 'mountain', 'park', 'street', 'travel'].includes(signals.sceneCategory);
  const outdoorBonus = isOutdoor ? 0.6 : 0.4;

  // Activity-suggesting scenes
  const activityScenes = ['sports', 'hiking', 'cooking', 'travel', 'beach', 'concert', 'museum'];
  const activitySignal = activityScenes.includes(signals.sceneCategory) ? 0.8 : 0.4;

  // Food photos show taste
  const foodBonus = signals.hasFood ? 0.2 : 0;

  const raw = categoryDiversity * 0.35 + outdoorBonus * 0.25 + activitySignal * 0.25 + foodBonus * 0.15;
  return Math.min(10, raw * 10);
}

// Patent: Novel dimension — D3: Approachability and warmth scoring.
// Combines genuine smile detection, eye contact, body language openness, and color warmth.
export function scoreApproachability(signals: SocialSignals): number {
  // Genuine smile is the strongest signal
  const smileScore = signals.smileIntensity * signals.expressionGenuineness;

  // Eye contact warmth
  const eyeScore = signals.eyeContactStrength;

  // Open body language
  const bodyScore = signals.bodyLanguageOpenness;

  // Warm tones feel more approachable
  const warmthBonus = signals.dominantColorWarmth > 0.5 ? 0.3 : 0;

  // Single person with good expression > group
  const soloBonus = signals.faceCount === 1 ? 0.2 : 0;

  const raw =
    smileScore * 0.30 +
    eyeScore * 0.25 +
    bodyScore * 0.20 +
    warmthBonus * 0.10 +
    soloBonus * 0.15;
  return Math.min(10, raw * 10);
}

// Patent: Novel dimension — D4: Taste and presentation scoring.
// Evaluates grooming, style coherence, and background curation.
export function scoreTastePresentation(signals: SocialSignals): number {
  const grooming = signals.groomingScore;
  const outfit = signals.outfitCoherence;
  const background = signals.backgroundCleanness;

  // Aspirational setting bonus
  const settingBonus = signals.settingAspirationLevel;

  // Good lighting on subject
  const lightingBonus = signals.lightingMood === 'warm' || signals.goldenHourDetected ? 0.3 : 0.1;

  const raw =
    grooming * 0.25 +
    outfit * 0.25 +
    background * 0.20 +
    settingBonus * 0.15 +
    lightingBonus * 0.15;
  return Math.min(10, raw * 10);
}

// Patent: Novel dimension — D5: Photo set composition (session-level).
// Evaluates how photos work together as a social profile set.
// Applied POST-SCORING to modify all photos in the session.
export function scorePhotoSetComposition(
  photoSignals: SocialSignals[],
  photoIndex: number,
): number {
  if (photoSignals.length <= 1) return 5.0; // neutral for single photos

  const categories = photoSignals.map(s => s.sceneCategory);
  const uniqueCategories = new Set(categories).size;

  // Variety bonus: more unique scenes = better set
  const varietyScore = Math.min(1, uniqueCategories / Math.min(photoSignals.length, 6));

  // Has a clear face shot?
  const hasFaceShot = photoSignals.some(s => s.primaryFaceConfidence > 0.8 && s.faceCount === 1);

  // Has a social photo?
  const hasSocial = photoSignals.some(s => s.faceCount >= 2 && s.faceCount <= 5);

  // Has an activity photo?
  const hasActivity = photoSignals.some(s =>
    ['sports', 'hiking', 'cooking', 'travel', 'concert'].includes(s.sceneCategory)
  );

  // Redundancy penalty: too many similar photos
  const currentCategory = photoSignals[photoIndex]?.sceneCategory;
  const sameCategory = categories.filter(c => c === currentCategory).length;
  const redundancyPenalty = sameCategory > 2 ? (sameCategory - 2) * 0.1 : 0;

  const completeness = (
    (hasFaceShot ? 0.3 : 0) +
    (hasSocial ? 0.3 : 0) +
    (hasActivity ? 0.2 : 0) +
    varietyScore * 0.2
  );

  const raw = completeness - redundancyPenalty;
  return Math.min(10, Math.max(0, raw * 10));
}

// Patent: Novel dimension — D6: Emotional availability scoring.
// Detects openness vs guardedness from expression and body language.
export function scoreEmotionalAvailability(signals: SocialSignals): number {
  // Genuine expression (not forced/posed)
  const genuineScore = signals.expressionGenuineness;

  // Open body language
  const opennessScore = signals.bodyLanguageOpenness;

  // Relaxed posture (low visual tension)
  const relaxedScore = 1 - signals.visualComplexity * 0.3;

  // Warm lighting = more emotionally open feel
  const warmthScore = signals.dominantColorWarmth;

  // Eye contact without intensity
  const eyeBalance = signals.eyeContactStrength > 0.3 && signals.eyeContactStrength < 0.9
    ? 0.8 : 0.5;

  const raw =
    genuineScore * 0.30 +
    opennessScore * 0.25 +
    relaxedScore * 0.15 +
    warmthScore * 0.15 +
    eyeBalance * 0.15;
  return Math.min(10, raw * 10);
}

// ─── Instagram Dimensions (I1–I5) ──────────────────────────

// Patent: Novel dimension — I1: Scroll-stop power scoring.
// Predicts visual stopping power in a feed context using contrast, vibrancy, uniqueness.
export function scoreScrollStopPower(signals: SocialSignals): number {
  // High contrast catches the eye
  const contrastScore = signals.contrastLevel;

  // Vibrancy pops in a feed
  const vibrancyScore = signals.colorVibrancy;

  // Uniqueness = pattern interrupt
  const uniqueScore = signals.uniquenessScore;

  // Strong subject prominence
  const prominenceScore = signals.subjectProminence;

  // Dramatic lighting
  const dramaBonus = signals.lightingMood === 'dramatic' ? 0.3 :
                     signals.goldenHourDetected ? 0.2 : 0;

  const raw =
    contrastScore * 0.25 +
    vibrancyScore * 0.20 +
    uniqueScore * 0.25 +
    prominenceScore * 0.15 +
    dramaBonus * 0.15;
  return Math.min(10, raw * 10);
}

// Patent: Novel dimension — I2: Aspirational quality scoring.
// Measures lifestyle aspiration signal — does this make someone wish they were there?
export function scoreAspirationalQuality(signals: SocialSignals): number {
  // Setting aspiration (luxury restaurant > fast food)
  const settingScore = signals.settingAspirationLevel;

  // Visual aesthetic quality
  const aestheticScore = (signals.colorVibrancy + signals.atmosphericDepth) / 2;

  // Golden hour / good lighting
  const lightingScore = signals.goldenHourDetected ? 0.9 :
                        signals.lightingMood === 'warm' ? 0.7 : 0.4;

  // Clean composition
  const compositionScore = signals.backgroundCleanness;

  const raw =
    settingScore * 0.30 +
    aestheticScore * 0.25 +
    lightingScore * 0.25 +
    compositionScore * 0.20;
  return Math.min(10, raw * 10);
}

// Patent: Novel dimension — I3: Aesthetic cohesion (session-level).
// Evaluates feed consistency — color palette, tone, style matching across photos.
export function scoreAestheticCohesion(
  photoSignals: SocialSignals[],
  photoIndex: number,
): number {
  if (photoSignals.length <= 1) return 7.0; // single photo = neutral-good

  const palettes = photoSignals
    .filter(s => s.setColorPalettes && s.setColorPalettes.length > 0)
    .flatMap(s => s.setColorPalettes ?? []);

  if (palettes.length === 0) return 5.0;

  // Color consistency: low hue variance = cohesive feed
  const hues = palettes.map(p => p.h);
  const meanHue = hues.reduce((s, h) => s + h, 0) / hues.length;
  const hueVariance = hues.reduce((s, h) => s + (h - meanHue) ** 2, 0) / hues.length;
  const hueConsistency = Math.max(0, 1 - hueVariance / 5000);

  // Saturation consistency
  const sats = palettes.map(p => p.s);
  const meanSat = sats.reduce((s, v) => s + v, 0) / sats.length;
  const satVariance = sats.reduce((s, v) => s + (v - meanSat) ** 2, 0) / sats.length;
  const satConsistency = Math.max(0, 1 - satVariance / 0.1);

  // Warmth consistency
  const warmths = photoSignals.map(s => s.dominantColorWarmth);
  const meanWarmth = warmths.reduce((s, w) => s + w, 0) / warmths.length;
  const warmthVariance = warmths.reduce((s, w) => s + (w - meanWarmth) ** 2, 0) / warmths.length;
  const warmthConsistency = Math.max(0, 1 - warmthVariance / 0.1);

  const raw = hueConsistency * 0.40 + satConsistency * 0.30 + warmthConsistency * 0.30;
  return Math.min(10, raw * 10);
}

// Patent: Novel dimension — I4: Engagement predictor scoring.
// Predicts like/comment ratio from visual signals proven to drive engagement.
export function scoreEngagementPredictor(signals: SocialSignals): number {
  // Face presence drives engagement
  const faceScore = signals.faceCount >= 1 ? 0.7 + Math.min(0.3, signals.primaryFaceConfidence * 0.3) : 0.3;

  // Smile drives likes
  const smileScore = signals.smileIntensity;

  // Vibrancy drives engagement
  const vibrancyScore = signals.colorVibrancy;

  // Food content drives engagement
  const foodBonus = signals.hasFood ? 0.3 : 0;

  // Animal content is engagement gold
  const animalBonus = signals.hasAnimal ? 0.4 : 0;

  const raw =
    faceScore * 0.25 +
    smileScore * 0.20 +
    vibrancyScore * 0.15 +
    foodBonus * 0.15 +
    animalBonus * 0.25;
  return Math.min(10, raw * 10);
}

// Patent: Novel dimension — I5: Shareability index scoring.
// Would someone share this? Uniqueness, wow factor, storytelling.
export function scoreShareability(signals: SocialSignals): number {
  // Uniqueness is the #1 shareability driver
  const uniqueScore = signals.uniquenessScore;

  // Visual wow factor
  const wowScore = (signals.contrastLevel + signals.colorVibrancy + signals.atmosphericDepth) / 3;

  // Emotional resonance makes people share
  const emotionScore = signals.smileIntensity > 0.5 ? 0.7 :
                       signals.lightingMood === 'dramatic' ? 0.6 : 0.3;

  // Story potential
  const storyScore = signals.visualComplexity > 0.3 && signals.visualComplexity < 0.7 ? 0.7 : 0.4;

  const raw =
    uniqueScore * 0.35 +
    wowScore * 0.25 +
    emotionScore * 0.20 +
    storyScore * 0.20;
  return Math.min(10, raw * 10);
}

// ─── Travel Dimensions (T1–T3) ─────────────────────────────

// Patent: Novel dimension — T1: "I was there" proof scoring.
// Authentic presence marker — subject naturally in scene, not tourist-pose.
export function scoreIWasThere(signals: SocialSignals): number {
  // Person present in scene
  const presenceScore = signals.faceCount >= 1 ? 0.8 : 0.2;

  // Not just a posed tourist shot (natural body language)
  const naturalScore = signals.bodyLanguageOpenness * signals.expressionGenuineness;

  // Scene interest behind them
  const sceneScore = signals.settingAspirationLevel;

  // Subject not too prominent (they're IN the scene, not blocking it)
  const balanceScore = signals.subjectProminence > 0.2 && signals.subjectProminence < 0.5
    ? 0.8 : 0.4;

  const raw =
    presenceScore * 0.30 +
    naturalScore * 0.25 +
    sceneScore * 0.25 +
    balanceScore * 0.20;
  return Math.min(10, raw * 10);
}

// Patent: Novel dimension — T2: Sensory translation scoring.
// Can you "feel" the place? Atmospheric depth, color temperature, texture.
export function scoreSensoryTranslation(signals: SocialSignals): number {
  // Atmospheric depth = feeling of being there
  const atmosphereScore = signals.atmosphericDepth;

  // Color warmth matches climate feeling
  const warmthScore = signals.dominantColorWarmth;

  // Visual complexity = rich sensory detail
  const detailScore = signals.visualComplexity > 0.4 && signals.visualComplexity < 0.8
    ? 0.8 : 0.4;

  // Golden hour / dramatic lighting transports you
  const lightingScore = signals.goldenHourDetected ? 0.9 :
                        signals.lightingMood === 'dramatic' ? 0.7 :
                        signals.lightingMood === 'warm' ? 0.6 : 0.3;

  // Vibrancy of colors
  const vibrancyScore = signals.colorVibrancy;

  const raw =
    atmosphereScore * 0.25 +
    warmthScore * 0.15 +
    detailScore * 0.20 +
    lightingScore * 0.20 +
    vibrancyScore * 0.20;
  return Math.min(10, raw * 10);
}

// Patent: Novel dimension — T3: Story fragment scoring.
// Does this photo tell part of a story? Action, interaction, discovery.
export function scoreStoryFragment(signals: SocialSignals): number {
  // Scene energy implies action/movement
  const energyScore = signals.sceneEnergy;

  // People interacting = story
  const interactionScore = signals.faceCount >= 2
    ? signals.eyeContactStrength * 0.5 + 0.5 // people looking at each other
    : signals.faceCount === 1 ? 0.5 : 0.3;

  // Visual complexity in the Goldilocks zone
  const complexityScore = signals.visualComplexity > 0.3 && signals.visualComplexity < 0.7
    ? 0.8 : 0.4;

  // Unique scene = more interesting story
  const uniqueScore = signals.uniquenessScore;

  // Food scenes tell a story
  const contextBonus = signals.hasFood ? 0.2 : signals.hasAnimal ? 0.15 : 0;

  const raw =
    energyScore * 0.25 +
    interactionScore * 0.20 +
    complexityScore * 0.20 +
    uniqueScore * 0.20 +
    contextBonus * 0.15;
  return Math.min(10, raw * 10);
}

// ─── Composite Social Calibration Score ────────────────────

// Patent: Social calibration composite — mode-specific weighted blend.
// Only active dimensions for the current mode contribute to the composite.
export function computeSocialCalibrationComposite(
  scores: SocialCalibrationScores,
  mode: string,
): number {
  const profile = SOCIAL_CALIBRATION_WEIGHTS[mode];
  if (!profile) return 0;

  let weightedSum = 0;
  let totalWeight = 0;

  for (const [key, weight] of Object.entries(profile.weights)) {
    const score = scores[key as SocialCalibrationKey];
    if (score !== null && score !== undefined && weight > 0) {
      weightedSum += score * weight;
      totalWeight += weight;
    }
  }

  return totalWeight > 0 ? Math.round((weightedSum / totalWeight) * 10) / 10 : 0;
}

// Patent: Social calibration blending — integrates social calibration with base+novel composite.
// finalScore = baseNovelComposite × (1 - blendRatio) + socialComposite × blendRatio
export function blendWithSocialCalibration(
  baseNovelComposite: number,
  socialComposite: number,
  mode: string,
): number {
  const profile = SOCIAL_CALIBRATION_WEIGHTS[mode];
  if (!profile || socialComposite === 0) return baseNovelComposite;

  const blendRatio = profile.blendRatio;
  const blended = baseNovelComposite * (1 - blendRatio) + socialComposite * blendRatio;
  return Math.round(blended * 10) / 10;
}

// ─── Full Social Scoring Pipeline ──────────────────────────

// Score all social calibration dimensions for a photo given its mode.
export function scoreSocialCalibration(
  signals: SocialSignals,
  mode: string,
  sessionSignals?: SocialSignals[], // all photos in session for session-level dims
): SocialCalibrationScores {
  const empty: SocialCalibrationScores = {
    socialProofSignal: null, lifestyleBreadth: null,
    approachabilityWarmth: null, tastePresentation: null,
    photoSetComposition: null, emotionalAvailability: null,
    scrollStopPower: null, aspirationalQuality: null,
    aestheticCohesion: null, engagementPredictor: null,
    shareabilityIndex: null,
    iWasThereProof: null, sensoryTranslation: null,
    storyFragment: null,
    groupEnergy: null, candidCapture: null, inclusiveFraming: null,
    peakAction: null, athleteIsolation: null, venueAtmosphere: null,
  };

  if (mode === 'social') {
    const photoIndex = signals.photoIndex ?? 0;
    return {
      ...empty,
      socialProofSignal: scoreSocialProof(signals),
      lifestyleBreadth: scoreLifestyleBreadth(signals),
      approachabilityWarmth: scoreApproachability(signals),
      tastePresentation: scoreTastePresentation(signals),
      photoSetComposition: sessionSignals
        ? scorePhotoSetComposition(sessionSignals, photoIndex)
        : null,
      emotionalAvailability: scoreEmotionalAvailability(signals),
    };
  }

  if (mode === 'instagram') {
    const photoIndex = signals.photoIndex ?? 0;
    return {
      ...empty,
      scrollStopPower: scoreScrollStopPower(signals),
      aspirationalQuality: scoreAspirationalQuality(signals),
      aestheticCohesion: sessionSignals
        ? scoreAestheticCohesion(sessionSignals, photoIndex)
        : null,
      engagementPredictor: scoreEngagementPredictor(signals),
      shareabilityIndex: scoreShareability(signals),
    };
  }

  if (mode === 'travel') {
    return {
      ...empty,
      iWasThereProof: scoreIWasThere(signals),
      sensoryTranslation: scoreSensoryTranslation(signals),
      storyFragment: scoreStoryFragment(signals),
    };
  }

  if (mode === 'event') {
    return {
      ...empty,
      groupEnergy: scoreGroupEnergy(signals),
      candidCapture: scoreCandidCapture(signals),
      inclusiveFraming: scoreInclusiveFraming(signals),
    };
  }

  if (mode === 'sports') {
    return {
      ...empty,
      peakAction: scorePeakAction(signals),
      athleteIsolation: scoreAthleteIsolation(signals),
      venueAtmosphere: scoreVenueAtmosphere(signals),
    };
  }

  return empty;
}

// ─── Event Mode (E1–E3) ──────────────────────────────────

// E1: Group Energy — collective liveliness from faces, expressions, scene energy
function scoreGroupEnergy(signals: SocialSignals): number {
  const faceFactor = Math.min(1, signals.faceCount / 4); // more faces = more energy, plateaus at 4
  const expressionFactor = signals.expressionGenuineness; // genuine expressions signal real energy
  const energyFactor = signals.sceneEnergy;
  const raw = (faceFactor * 0.35 + expressionFactor * 0.35 + energyFactor * 0.30) * 10;
  return Math.round(Math.min(10, Math.max(1, raw)) * 10) / 10;
}

// E2: Candid Capture — unposed moment quality
function scoreCandidCapture(signals: SocialSignals): number {
  // High genuineness + moderate body openness = candid; high eye contact = possibly posed
  const candidness = signals.expressionGenuineness * 0.5 +
    signals.bodyLanguageOpenness * 0.3 +
    (1 - signals.eyeContactStrength * 0.5) * 0.2; // less direct gaze = more candid
  const raw = candidness * 10;
  return Math.round(Math.min(10, Math.max(1, raw)) * 10) / 10;
}

// E3: Inclusive Framing — everyone visible, balanced composition
function scoreInclusiveFraming(signals: SocialSignals): number {
  const multiPerson = Math.min(1, signals.faceCount / 3); // at least 3 people for events
  const prominence = signals.subjectProminence; // higher = more focused on one person (less inclusive)
  const complexity = signals.visualComplexity; // moderate complexity = good group scenes
  const inclusiveness = multiPerson * 0.40 +
    (1 - Math.abs(prominence - 0.3)) * 0.30 + // 0.3 prominence = balanced, not dominated
    Math.min(1, complexity * 1.5) * 0.30;
  const raw = inclusiveness * 10;
  return Math.round(Math.min(10, Math.max(1, raw)) * 10) / 10;
}

// ─── Sports Mode (S1–S3) ─────────────────────────────────

// S1: Peak Action — decisive moment capture, scene energy, implied motion
function scorePeakAction(signals: SocialSignals): number {
  const energy = signals.sceneEnergy; // high energy = action scene
  const vibrancy = signals.colorVibrancy; // vibrant scenes often indicate outdoor/action
  const depth = signals.atmosphericDepth; // depth suggests field/court/venue
  const raw = (energy * 0.45 + vibrancy * 0.25 + depth * 0.30) * 10;
  return Math.round(Math.min(10, Math.max(1, raw)) * 10) / 10;
}

// S2: Athlete Isolation — subject separation from background
function scoreAthleteIsolation(signals: SocialSignals): number {
  const prominence = signals.subjectProminence; // higher = better isolation
  const cleanBg = signals.backgroundCleanness; // cleaner bg = more isolation
  const depth = signals.atmosphericDepth;
  const raw = (prominence * 0.40 + cleanBg * 0.35 + depth * 0.25) * 10;
  return Math.round(Math.min(10, Math.max(1, raw)) * 10) / 10;
}

// S3: Venue Atmosphere — energy of the setting
function scoreVenueAtmosphere(signals: SocialSignals): number {
  const complexity = signals.visualComplexity; // venues are visually complex
  const energy = signals.sceneEnergy;
  const vibrancy = signals.colorVibrancy;
  const raw = (complexity * 0.35 + energy * 0.35 + vibrancy * 0.30) * 10;
  return Math.round(Math.min(10, Math.max(1, raw)) * 10) / 10;
}
